/* eslint-disable react/jsx-props-no-spreading */
import { FC, ComponentProps, useMemo, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Box, Typography, Svg } from '@altermaind/web/ui';
import { WrappedInput } from '../WrappedInput';
import { StarsWrapper, StarButton, HiddenInput } from './styles';

type Props = {
  label?: string;
  values?: (string | number)[];
  starSize?: ComponentProps<typeof Svg>['size'];
  showValue?: boolean;
} & ComponentProps<typeof WrappedInput>;

export const WrappedRatingInput: FC<Props> = ({
  label,
  values,
  starSize = 's',
  showValue = false,
  name,
  control,
  ...props
}) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [canHover, setCanHover] = useState(false);

  const ratingValues = useMemo(() => (values?.length ? values : [1, 2, 3, 4, 5]), [values]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHover(mq.matches);
    update();
    mq.addEventListener?.('change', update);

    return () => {
      mq.removeEventListener?.('change', update);
    };
  }, []);

  return (
    <Box>
      {label ? (
        <Box mb='xxs'>
          <Typography variant='p3/light'>{label}</Typography>
        </Box>
      ) : null}

      <Controller
        control={control}
        name={name}
        defaultValue={props.defaultValue ?? 0}
        render={({ field, fieldState }) => {
          const currentValue = Number(field.value ?? 0);
          const visualValue = (canHover ? hoverValue : null) ?? currentValue;

          return (
            <Box>
              {/* hidden input to preserve the WrappedInput pattern + RHF integration */}
              <HiddenInput
                // @ts-ignore
                control={control}
                name={name}
                {...props}
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                type='hidden'
              />

              <StarsWrapper
                role='radiogroup'
                aria-label={label || name}
                onPointerLeave={() => {
                  if (!canHover) return;
                  setHoverValue(null);
                  field.onBlur();
                }}
              >
                {ratingValues.map(v => {
                  const n = Number(v);
                  const active = visualValue >= n;

                  return (
                    <StarButton
                      key={v}
                      type='button'
                      role='radio'
                      aria-checked={currentValue === n}
                      aria-label={`${n}`}
                      disabled={props.disabled}
                      active={active}
                      onPointerEnter={() => {
                        if (!canHover || props.disabled) {
                          return;
                        }
                        setHoverValue(n);
                      }}
                      onPointerDown={e => {
                        if (props.disabled) return;
                        // prevents "hover/focus" from sticking on mobile
                        e.currentTarget.setPointerCapture?.(e.pointerId);
                        setHoverValue(null);
                        field.onChange(n);
                        field.onBlur();
                      }}
                      onClick={() => {
                        // safety: some browsers call click after pointerdown
                        if (props.disabled) return;
                        setHoverValue(null);
                      }}
                    >
                      <Svg name={active ? 'star' : 'emptyStar'} size={starSize} />
                    </StarButton>
                  );
                })}
              </StarsWrapper>

              {showValue ? (
                <Box mt='xxs'>
                  <Typography variant='p3/light'>{currentValue || ''}</Typography>
                </Box>
              ) : null}

              {fieldState?.error?.message ? (
                <Box mt='xxs'>
                  <Typography variant='p3/light'>{fieldState.error.message}</Typography>
                </Box>
              ) : null}
            </Box>
          );
        }}
      />
    </Box>
  );
};
